/**
 * Job costing + time tracking inputs (PRD v2 §7, V1 tail).
 *
 * A leaf module like schemas/request.ts: it imports the idempotency envelope from the
 * spine and nothing else from the schema layer, so it can never close an import cycle.
 */
import { z } from 'zod';
import { idempotency } from './spine';
import { JOB_COST_KINDS } from '../costing';

const optionalText = (max: number) =>
  z.string().trim().max(max).optional().or(z.literal(''));

// ── Timers ───────────────────────────────────────────────────────────────────
/** Start the caller's clock. A visit is optional — travel and shop time are real labor. */
/**
 * How long ago the tap happened, measured on the phone when the request went out (build-map 062:
 * the web's `tapAgeLink`). A tap made with no signal is sent later from the offline queue; without
 * this the server stamped the time it ARRIVED, and since ticket 13 those hours bill. An age, not a
 * time, so a phone clock that is off cancels out. Negative means the phone's clock went back while
 * the tap waited: the server refuses it, as it does one over 4 hours (the owner, 2026-10-14).
 */
const tapAgo = z.coerce.number().int().optional();

export const timerStartInput = idempotency.extend({
  jobId: z.string().uuid(),
  visitId: z.string().uuid().optional(),
  notes: optionalText(500),
  agoMs: tapAgo,
});
export type TimerStartInput = z.infer<typeof timerStartInput>;

export const timerStopInput = idempotency.extend({
  timeEntryId: z.string().uuid(),
  notes: optionalText(500),
  agoMs: tapAgo,
});
export type TimerStopInput = z.infer<typeof timerStopInput>;

// ── Manual time ──────────────────────────────────────────────────────────────
/** Office-entered labor: someone forgot to hit the button, which happens every day. */
export const timeEntryCreateInput = idempotency
  .extend({
    jobId: z.string().uuid(),
    visitId: z.string().uuid().optional(),
    /** Whose labor this is. Defaults to the caller. */
    userId: z.string().uuid().optional(),
    startedAt: z.coerce.date(),
    endedAt: z.coerce.date(),
    billable: z.boolean().optional(),
    notes: optionalText(500),
  })
  .refine((v) => v.endedAt > v.startedAt, {
    message: 'End time must be after the start time',
    path: ['endedAt'],
  });
export type TimeEntryCreateInput = z.infer<typeof timeEntryCreateInput>;

export const timeEntryDeleteInput = idempotency.extend({
  timeEntryId: z.string().uuid(),
});
export type TimeEntryDeleteInput = z.infer<typeof timeEntryDeleteInput>;

// ── Non-labor costs ──────────────────────────────────────────────────────────
/** What the job COST us — never a price. Inventory (V3) writes these at moving average. */
export const jobCostCreateInput = idempotency.extend({
  jobId: z.string().uuid(),
  kind: z.enum(JOB_COST_KINDS),
  description: z.string().trim().min(1).max(200),
  qty: z.number().positive().max(100000).optional(),
  unitCostCents: z.number().int().min(0).max(1_000_000_000),
  vendorName: optionalText(120),
  incurredOn: z.string().date().optional(),
  billable: z.boolean().optional(),
});
export type JobCostCreateInput = z.infer<typeof jobCostCreateInput>;

export const jobCostDeleteInput = idempotency.extend({
  jobCostId: z.string().uuid(),
});
export type JobCostDeleteInput = z.infer<typeof jobCostDeleteInput>;

// ── Labor rates ──────────────────────────────────────────────────────────────
/** What a person costs the business per hour. Null clears it back to "unknown". */
export const memberCostRateInput = idempotency.extend({
  userId: z.string().uuid(),
  hourlyCostCents: z.number().int().min(0).max(100_000_000).nullable(),
});
export type MemberCostRateInput = z.infer<typeof memberCostRateInput>;
